import React from "react";
import * as Icons from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Combobox } from "../../ui/combobox";
import IconPicker from "../../ui/icon-picker";
import { googleSheetsCache } from "../../../stores/google-sheets-cache";
import { ConfigFieldsProps } from "./types";
import { DynamicSourceSelector } from "./DynamicSourceSelector";

export function MetricCardConfig({
  editedComponent,
  setEditedComponent,
  updateConfig,
  availableComponents,
  pageComponents,
  integrations,
}: ConfigFieldsProps) {
  const metricDataComponents = availableComponents.filter(
    (c) =>
      c.id !== editedComponent.id && ["table", "kanban"].includes(c.type),
  );

  const dynamicSourceField = editedComponent.config.dynamicSourceField;
  const dataSourceMode: "manual" | "component" | "dynamic" =
    dynamicSourceField !== undefined
      ? "dynamic"
      : editedComponent.config.sourceComponentId !== undefined
        ? "component"
        : "manual";

  // Em modo dinâmico, usa a primeira opção do filtro "Tabela Vinculada" só como
  // amostra para popular os selects de campo/agregação (a fonte real é resolvida
  // em runtime conforme a seleção ativa do filtro). O filtro precisa estar na
  // mesma página (activeFilters é um estado por página).
  const dynamicFilterComp = pageComponents.find(
    (c) => c.type === "filter" && c.config?.field === dynamicSourceField,
  );
  const sampleSourceId = dataSourceMode === "dynamic"
    ? dynamicFilterComp?.config?.options?.[0]?.value
    : editedComponent.config.sourceComponentId;

  const selectedMetricSource = metricDataComponents.find(
    (c) => c.id === sampleSourceId,
  );

  const isMetricNumericField = (fieldName: string, data: any[]) => {
    if (!data || data.length === 0) return false;
    const sampleSize = Math.min(20, data.length);
    let numericCount = 0;
    for (let i = 0; i < sampleSize; i++) {
      const value = data[i][fieldName];
      if (value !== null && value !== undefined && value !== "") {
        const num = Number(value);
        if (!isNaN(num)) numericCount++;
      }
    }
    return numericCount / sampleSize >= 0.8;
  };

  let metricSourceData: any[] = [];

  if (selectedMetricSource?.config?.googleSheets?.enabled) {
    const sourceSpreadsheetId = selectedMetricSource.config.googleSheets.integrationId
      ? integrations.find((i) => i.id === selectedMetricSource.config.googleSheets.integrationId)?.config?.spreadsheetId
      : selectedMetricSource.config.googleSheets.spreadsheetId;
    const cached = googleSheetsCache.get(
      sourceSpreadsheetId,
      selectedMetricSource.config.googleSheets.range || "Sheet1",
    );
    if (cached && cached.data.length > 0) {
      metricSourceData = cached.data;
    }
  } else if (
    selectedMetricSource?.type === "kanban" &&
    selectedMetricSource?.config?.columns
  ) {
    metricSourceData = selectedMetricSource.config.columns.flatMap(
      (column: any) =>
        (column.cards || []).map((card: any) => ({
          ...card,
          columnId: column.id,
          columnTitle: column.title,
        })),
    );
  } else {
    metricSourceData = selectedMetricSource?.config?.dataSource?.data || [];
  }

  const metricAvailableFields =
    metricSourceData.length > 0
      ? Object.keys(metricSourceData[0])
      : selectedMetricSource?.config?.columns?.map((col: any) => col.field) || [];

  const metricNumericColumns = metricAvailableFields
    .filter((field: string) => isMetricNumericField(field, metricSourceData))
    .map((field: string) => ({
      field,
      label:
        selectedMetricSource?.config?.columns?.find(
          (col: any) => col.field === field,
        )?.label || field,
    }));

  return (
    <>
      <div className="flex gap-4 items-end">
        <div className="flex-1 space-y-2">
          <Label htmlFor="title">Título</Label>
          <Input
            id="title"
            type="text"
            value={editedComponent.config.title || ""}
            onChange={(e) => updateConfig("title", e.target.value)}
          />
        </div>
        <div className="w-[70px] space-y-2">
          <Label htmlFor="icon" className="block">Ícone</Label>
          <IconPicker
            value={editedComponent.config.icon || ""}
            onChange={(iconName) => updateConfig("icon", iconName)}
            trigger={
              <Button variant="outline" size="icon" className="w-10 h-10">
                {editedComponent.config.icon ? (
                  React.createElement(
                    (Icons as any)[editedComponent.config.icon] || Icons.Smile,
                    { className: "w-4 h-4" },
                  )
                ) : (
                  <Icons.Smile className="w-4 h-4" />
                )}
              </Button>
            }
          />
        </div>
      </div>

      <div className="border-t pt-4 mt-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="dataSource">Fonte de Dados</Label>
          <Select
            value={dataSourceMode}
            onValueChange={(value) => {
              if (value === "manual") {
                setEditedComponent((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    config: {
                      ...prev.config,
                      sourceComponentId: undefined,
                      dynamicSourceField: undefined,
                      aggregationField: undefined,
                      aggregationType: undefined,
                    },
                  };
                });
              } else if (value === "component") {
                setEditedComponent((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    config: {
                      ...prev.config,
                      sourceComponentId: prev.config.sourceComponentId || "",
                      dynamicSourceField: undefined,
                    },
                  };
                });
              } else if (value === "dynamic") {
                setEditedComponent((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    config: {
                      ...prev.config,
                      dynamicSourceField: prev.config.dynamicSourceField || "",
                      sourceComponentId: undefined,
                      aggregationType: prev.config.aggregationType || "sum",
                    },
                  };
                });
              }
            }}
          >
            <SelectTrigger id="dataSource">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Dados Manuais</SelectItem>
              <SelectItem value="component">
                Usar dados de outros componentes
              </SelectItem>
              <SelectItem value="dynamic">Dinâmica (via filtro)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {dataSourceMode === "manual" && (
          <div className="p-4 bg-accent border border-primary/20 rounded-md">
            <p className="text-sm text-foreground">
              <strong>Dica:</strong> O valor da métrica pode ser editado
              clicando no botão de dados que aparece no próprio componente.
            </p>
          </div>
        )}

        {dataSourceMode === "dynamic" && (
          <DynamicSourceSelector
            pageComponents={pageComponents}
            value={dynamicSourceField}
            onChange={(field) => updateConfig("dynamicSourceField", field)}
          />
        )}

        {dataSourceMode === "component" && (
          <div className="space-y-2">
            <Label htmlFor="selectSourceComponent">Componente de Origem</Label>
            <Select
              value={editedComponent.config.sourceComponentId || ""}
              onValueChange={(value) => {
                const newSourceId = value || "";
                setEditedComponent((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    config: {
                      ...prev.config,
                      sourceComponentId: newSourceId,
                      aggregationField: newSourceId
                        ? prev.config.aggregationField
                        : undefined,
                      aggregationType: newSourceId
                        ? prev.config.aggregationType || "sum"
                        : undefined,
                    },
                  };
                });
              }}
            >
              <SelectTrigger id="selectSourceComponent">
                <SelectValue placeholder="Selecione uma tabela" />
              </SelectTrigger>
              <SelectContent>
                {metricDataComponents.map((comp) => {
                  const pageName = (comp as any)._pageName;
                  const displayName = pageName
                    ? `${comp.config.title || comp.type} (Página: ${pageName})`
                    : comp.config.title || `Tabela - ${comp.id.substring(0, 8)}`;
                  return (
                    <SelectItem key={comp.id} value={comp.id}>
                      {displayName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {(() => {
              const selected = metricDataComponents.find(
                (c) => c.id === editedComponent.config.sourceComponentId,
              );
              if (selected?.type === "kanban" && selected?.config?.columns) {
                const kanbanFields = [
                  { value: "columnId", label: "Coluna" },
                  { value: "priority", label: "Prioridade" },
                ];
                const columnOptions = selected.config.columns.map(
                  (col: any) => ({ value: col.id, label: col.title }),
                );
                const priorityOptions = [
                  { value: "low", label: "Baixa" },
                  { value: "medium", label: "Média" },
                  { value: "high", label: "Alta" },
                ];
                const filterField =
                  editedComponent.config.kanbanFilterField || "columnId";
                const filterValue =
                  editedComponent.config.kanbanFilterValue || "";
                const valueOptions =
                  filterField === "columnId"
                    ? columnOptions
                    : filterField === "priority"
                      ? priorityOptions
                      : [];
                return (
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label>Filtrar por</Label>
                      <Select
                        value={filterField}
                        onValueChange={(val) =>
                          updateConfig("kanbanFilterField", val)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {kanbanFields.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Label>Valor</Label>
                      <Select
                        value={filterValue}
                        onValueChange={(val) =>
                          updateConfig("kanbanFilterValue", val)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {valueOptions.map(
                            (opt: { value: string; label: string }) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        {editedComponent.config.sourceComponentId || dynamicSourceField ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="aggregationType">Tipo de Agregação</Label>
              <Select
                value={editedComponent.config.aggregationType || "sum"}
                onValueChange={(value) => updateConfig("aggregationType", value)}
              >
                <SelectTrigger id="aggregationType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sum">Soma</SelectItem>
                  <SelectItem value="avg">Média</SelectItem>
                  <SelectItem value="count">Contagem</SelectItem>
                  <SelectItem value="min">Mínimo</SelectItem>
                  <SelectItem value="max">Máximo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editedComponent.config.aggregationType !== "count" && (
              <div className="space-y-2">
                <Label htmlFor="aggregationField">Campo para Calcular</Label>
                <Select
                  value={editedComponent.config.aggregationField || ""}
                  onValueChange={(value) =>
                    updateConfig("aggregationField", value)
                  }
                >
                  <SelectTrigger id="aggregationField">
                    <SelectValue placeholder="Selecione um campo numérico" />
                  </SelectTrigger>
                  <SelectContent>
                    {metricNumericColumns.map((col: any) => (
                      <SelectItem key={col.field} value={col.field}>
                        {col.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {metricNumericColumns.length === 0 && (
                  <p className="text-xs text-amber-600">
                    Nenhum campo numérico encontrado na tabela
                  </p>
                )}
              </div>
            )}
          </>
        ) : null}

        {(editedComponent.config.sourceComponentId || dynamicSourceField) && (
          <div className="border-t pt-4 mt-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                Filtros (Opcional)
              </Label>
              <p className="text-xs text-muted-foreground">
                Filtre os dados antes de calcular a métrica
              </p>
            </div>

            <div className="flex gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="filterField">Coluna para filtrar</Label>
                <Combobox
                  options={metricAvailableFields.map((field: string) => ({
                    value: field,
                    label:
                      selectedMetricSource?.config?.columns?.find(
                        (col: any) => col.field === field,
                      )?.label || field,
                  }))}
                  value={editedComponent.config.filterField || ""}
                  onValueChange={(value) => {
                    if (value) {
                      updateConfig("filterField", value);
                      updateConfig("filterValue", "");
                    }
                  }}
                  placeholder="Selecione uma coluna..."
                  searchPlaceholder="Buscar coluna..."
                  emptyText="Nenhuma coluna encontrada."
                />
              </div>

              {editedComponent.config.filterField && (
                <div className="flex-1 space-y-2">
                  <Label htmlFor="filterValue">Valor</Label>
                  <Combobox
                    options={(() => {
                      const field = editedComponent.config.filterField;
                      const uniqueValues = [
                        ...new Set(
                          metricSourceData
                            .map((item: any) => item[field])
                            .filter(
                              (val: any) =>
                                val !== null &&
                                val !== undefined &&
                                val !== "",
                            ),
                        ),
                      ].map((val) => ({
                        value: String(val),
                        label: String(val),
                      }));
                      return uniqueValues;
                    })()}
                    value={editedComponent.config.filterValue || ""}
                    onValueChange={(value) =>
                      updateConfig("filterValue", value)
                    }
                    placeholder="Selecione um valor..."
                    searchPlaceholder="Buscar valor..."
                    emptyText="Nenhum valor encontrado."
                  />
                </div>
              )}
            </div>

            {editedComponent.config.filterField &&
              editedComponent.config.filterValue && (
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
                  <p className="text-xs text-foreground">
                    <strong>Filtro ativo:</strong>{" "}
                    {selectedMetricSource?.config?.columns?.find(
                      (col: any) =>
                        col.field === editedComponent.config.filterField,
                    )?.label || editedComponent.config.filterField}
                    {" = "}
                    {editedComponent.config.filterValue}
                  </p>
                </div>
              )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="format">Formato</Label>
        <Select
          value={editedComponent.config.format || "number"}
          onValueChange={(value) => updateConfig("format", value)}
        >
          <SelectTrigger id="format">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="number">Número</SelectItem>
            <SelectItem value="currency">Moeda</SelectItem>
            <SelectItem value="percentage">Porcentagem</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="trend">Variação (%)</Label>
        <Input
          id="trend"
          type="number"
          step="0.1"
          value={editedComponent.config.trend?.value || 0}
          onChange={(e) => {
            const trendValue = Number(e.target.value);
            updateConfig(
              "trend",
              trendValue === 0
                ? undefined
                : { value: trendValue, isPositive: trendValue > 0 },
            );
          }}
          placeholder="0 para não mostrar tendência"
        />
      </div>
    </>
  );
}
